import { useDebouncer } from '@tanstack/react-pacer';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import config from '../../../config';
import {
  createCurrentGuestCountLabels,
  createGuestCount,
  createSteps,
  getCategoryLabel,
} from '../../../forms/BookingDatesForm/BookingDatesForm.steps';
import { isFlexPass, selectedDateOverlapsOpenBooking } from '../../../util/annualPass';
import { isCampingGroundListing } from '../../../util/data';
import useAppSelector from '../../../util/hooks/useAppSelector';
import usePass from '../../../util/hooks/usePass';
import { findOptionsForSelectFilter } from '../../../util/search';
import { REQUEST_BOOKING } from '../../../util/transaction.default';
import {
  buildSubmitValues,
  createInitialFormValues,
  detectBookingDataChange,
  selectLineItems,
} from './BookingPanel.helpers';

const DEBOUNCE_MS = 500;
const NO_DATES = [null, null];

const countSelected = (items) => Object.values(items || {}).filter((item) => item?.add).length;

/**
 * Report booking data changes to the price preview: date changes right away,
 * guest/extra/custom edits debounced. The callback is read through a ref so a
 * callback swapped during the debounce window (e.g. after a voucher was applied)
 * is the one that fires.
 *
 * @returns {() => void} Drops a pending debounced report
 */
const useReportBookingData = ({
  bookingDates,
  guestCount,
  formExtraLineItems,
  formCustomLineItems,
  listingExtraLineItems,
  listingCustomLineItems,
  onBookingDataChange,
}) => {
  const onChangeRef = useRef(onBookingDataChange);
  useEffect(() => {
    onChangeRef.current = onBookingDataChange;
  }, [onBookingDataChange]);

  // maybeExecute and cancel are stable per debouncer instance
  const { maybeExecute: debouncedReport, cancel: cancelReport } = useDebouncer(
    (data) => onChangeRef.current?.(data),
    { wait: DEBOUNCE_MS },
  );

  // bookingDates starts at [null, null] so pre-filled initialDates are reported
  // on the first run and trigger the line-items fetch.
  const prevRef = useRef({
    bookingDates: NO_DATES,
    guestCount,
    extraLineItems: formExtraLineItems,
    customLineItems: formCustomLineItems,
  });

  useEffect(() => {
    if (!onChangeRef.current) return;

    const next = {
      bookingDates,
      guestCount,
      extraLineItems: formExtraLineItems,
      customLineItems: formCustomLineItems,
    };
    const change = detectBookingDataChange(prevRef.current, next);
    prevRef.current = next;
    if (change === 'none') return;

    const data = {
      bookingDates,
      guestCount,
      extraLineItems: selectLineItems(formExtraLineItems, listingExtraLineItems),
      customLineItems: selectLineItems(formCustomLineItems, listingCustomLineItems),
    };
    if (change === 'debounced') {
      debouncedReport(data);
    } else {
      onChangeRef.current(data);
    }
  }, [
    bookingDates,
    debouncedReport,
    formCustomLineItems,
    formExtraLineItems,
    guestCount,
    listingCustomLineItems,
    listingExtraLineItems,
  ]);

  return cancelReport;
};

/**
 * Hard-block the same-date annual-pass overlap that the backend otherwise strips
 * silently (see selectedDateOverlapsOpenBooking / the backend
 * `thisTransactionIsTheSameDateAsAnOpenTransaction` guard). The calendar already
 * greys out overlapping slots, but that relies on the open-bookings list having
 * loaded before the time slots, a race that let users book the same period twice
 * and lose the discount with no explanation. Mirrors the backend scope:
 * production-only, and only the regular annual pass (25-pass / FlexPass are
 * exempt from the open-booking limits).
 */
const useAnnualPassDateOverlapError = (startDate) => {
  const intl = useIntl();
  const currentUserOpenBookings = useAppSelector((state) => state.user.currentUserOpenBookings);
  const [passIsValid, pass] = usePass();
  const passWithOpenBookingLimit = !(pass?.twentyFivePass || isFlexPass(pass));

  return process.env.REACT_APP_ENV === 'production' &&
    passIsValid &&
    passWithOpenBookingLimit &&
    selectedDateOverlapsOpenBooking(startDate, currentUserOpenBookings)
    ? intl.formatMessage({ id: 'BookingDatesForm.annualPassDateOverlap' })
    : null;
};

/**
 * Owns the booking form (dates, guests, extras, voucher step) so the panel can
 * keep its state across layout switches. Back to the defaults when the listing
 * changes.
 *
 * @param {object} params
 * @param {object} params.currentListing
 * @param {Array|null} [params.initialDates] - Validated [moment, moment] from the deep link
 * @param {boolean|string|null} [params.showAnnualPassError] - Count-based limit error from the panel
 * @param {object|null} [params.appliedVoucher]
 * @param {object|null} [params.voucherValidationError]
 * @param {boolean} [params.voucherValidationInProgress]
 * @param {boolean} [params.fetchLineItemsInProgress]
 * @param {(data: object) => void} [params.onBookingDataChange]
 * @param {(code: string) => void} params.onApplyVoucher
 * @param {(values: object) => void} params.onSubmit - Gets the payload of `buildSubmitValues`
 */
const useBookingForm = ({
  currentListing,
  initialDates = null,
  showAnnualPassError = false,
  appliedVoucher = null,
  voucherValidationError = null,
  voucherValidationInProgress = false,
  fetchLineItemsInProgress = false,
  onBookingDataChange,
  onApplyVoucher,
  onSubmit,
}) => {
  const intl = useIntl();
  const { publicData } = currentListing.attributes;
  const listingId = currentListing.id.uuid;

  const initialBookingDates = useMemo(
    () => (initialDates && initialDates[0] && initialDates[1] ? initialDates : NO_DATES),
    [initialDates],
  );
  const isCampingGround = isCampingGroundListing(currentListing);
  const guestOptions = useMemo(
    () => createGuestCount(publicData.guestOptions, isCampingGround, intl),
    [publicData.guestOptions, isCampingGround, intl],
  );
  const countLabels = useMemo(() => createCurrentGuestCountLabels(intl), [intl]);

  const { control, watch, setValue, handleSubmit, reset } = useForm({
    defaultValues: createInitialFormValues(initialBookingDates, guestOptions),
  });
  const bookingDates = watch('bookingDates');
  const guestCount = watch('guestCount');
  const formExtraLineItems = useWatch({ control, name: 'extraLineItems' });
  const formCustomLineItems = useWatch({ control, name: 'customLineItems' });

  const listingExtraLineItems = useMemo(() => publicData.extraLineItems || {}, [publicData.extraLineItems]);
  const listingCustomLineItems = useMemo(() => publicData.customLineItems || {}, [publicData.customLineItems]);

  const cancelReport = useReportBookingData({
    bookingDates,
    guestCount,
    formExtraLineItems,
    formCustomLineItems,
    listingExtraLineItems,
    listingCustomLineItems,
    onBookingDataChange,
  });

  // Always reset the end date when the start date changes so the user re-selects it.
  const [currentStartDate, currentEndDate] = bookingDates;
  const prevStartDateRef = useRef(currentStartDate);
  useEffect(() => {
    const prevStartDate = prevStartDateRef.current;
    const startDateChanged = prevStartDate && currentStartDate
      && !moment(prevStartDate).isSame(currentStartDate, 'day');

    if (startDateChanged && currentEndDate) {
      setValue('bookingDates', [currentStartDate, null]);
    }
    prevStartDateRef.current = currentStartDate;
  }, [currentStartDate, currentEndDate, setValue]);

  // A different listing starts from the defaults again (the first render is the baseline).
  // A report still pending for the previous listing is dropped.
  const prevListingIdRef = useRef(listingId);
  useEffect(() => {
    if (prevListingIdRef.current === listingId) return;
    prevListingIdRef.current = listingId;
    prevStartDateRef.current = initialBookingDates[0];
    cancelReport();
    reset(createInitialFormValues(initialBookingDates, guestOptions));
  }, [listingId, initialBookingDates, guestOptions, reset, cancelReport]);

  const annualPassDateOverlapError = useAnnualPassDateOverlapError(currentStartDate);
  // showAnnualPassError covers the count-based limits (already shown by the
  // parent panel); merge the date overlap so both gate the submit equally.
  const effectiveAnnualPassError = showAnnualPassError || annualPassDateOverlapError;

  const extraSelectedCount = countSelected(formExtraLineItems);
  const customSelectedCount = countSelected(formCustomLineItems);
  const steps = useMemo(() => createSteps({
    appliedVoucher,
    control,
    currentListing,
    customLineItemsSelectedCount: customSelectedCount,
    disabled: fetchLineItemsInProgress,
    extraLineItemsSelectedCount: extraSelectedCount,
    intl,
    onApplyVoucher,
    voucherValidationError,
    voucherValidationInProgress,
  }), [appliedVoucher, control, currentListing, customSelectedCount, extraSelectedCount, fetchLineItemsInProgress, intl, onApplyVoucher, voucherValidationError, voucherValidationInProgress]);

  const categoryKey = publicData.category || REQUEST_BOOKING;
  const categoryLabel = useMemo(() => {
    const categoryOptions = findOptionsForSelectFilter('category', config.custom.filters);
    return <span>{getCategoryLabel(categoryOptions, categoryKey, intl)}</span>;
  }, [categoryKey, intl]);

  const getGuestDisplayValue = useCallback((value = {}) => Object.keys(value)
    .map((key) => (value[key] === 0 || !countLabels[key] ? null : countLabels[key](value[key])))
    .filter(Boolean)
    .join(', '), [countLabels]);

  const setBookingDates = useCallback((value) => setValue('bookingDates', value), [setValue]);
  const setGuestCount = useCallback((value) => setValue('guestCount', value), [setValue]);
  const submit = useCallback(() => handleSubmit((values) => {
    const payload = buildSubmitValues(values, publicData);
    if (payload) onSubmit(payload);
  })(), [handleSubmit, publicData, onSubmit]);

  return {
    control,
    bookingDates,
    guestCount,
    guestOptions,
    countLabels,
    hasDatesSelected: Boolean(currentStartDate && currentEndDate),
    annualPassDateOverlapError,
    effectiveAnnualPassError,
    steps,
    categoryLabel,
    getGuestDisplayValue,
    setBookingDates,
    setGuestCount,
    submit,
  };
};

export default useBookingForm;
